const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

function histogramPercentile(histogram, pixelCount, ratio) {
  const target = Math.floor(pixelCount * ratio);
  let seen = 0;
  for (let value = 0; value < histogram.length; value += 1) {
    seen += histogram[value];
    if (seen > target) return value;
  }
  return 255;
}

function analyzePixels(imageData) {
  const { data, width, height } = imageData;
  const pixelCount = width * height;
  const luminance = new Uint8Array(pixelCount);
  const histogram = new Uint32Array(256);
  let sum = 0;

  for (let pixel = 0, offset = 0; pixel < pixelCount; pixel += 1, offset += 4) {
    const value = Math.round(data[offset] * 0.2126 + data[offset + 1] * 0.7152 + data[offset + 2] * 0.0722);
    luminance[pixel] = value;
    histogram[value] += 1;
    sum += value;
  }

  let laplacianSum = 0;
  let laplacianSquaredSum = 0;
  let laplacianCount = 0;
  for (let y = 1; y < height - 1; y += 1) {
    for (let x = 1; x < width - 1; x += 1) {
      const index = y * width + x;
      const value = 4 * luminance[index]
        - luminance[index - 1]
        - luminance[index + 1]
        - luminance[index - width]
        - luminance[index + width];
      laplacianSum += value;
      laplacianSquaredSum += value * value;
      laplacianCount += 1;
    }
  }
  const laplacianMean = laplacianCount ? laplacianSum / laplacianCount : 0;
  const laplacianVariance = laplacianCount
    ? Math.max(0, laplacianSquaredSum / laplacianCount - laplacianMean * laplacianMean)
    : 0;

  const darkClipped = histogram.slice(0, 9).reduce((total, count) => total + count, 0) / pixelCount;
  const brightClipped = histogram.slice(247).reduce((total, count) => total + count, 0) / pixelCount;
  const meanLuminance = sum / pixelCount;
  const p05 = histogramPercentile(histogram, pixelCount, 0.05);
  const p95 = histogramPercentile(histogram, pixelCount, 0.95);

  const sharpnessScore = Math.round(100 * clamp(
    (Math.log1p(laplacianVariance) - Math.log1p(22)) / (Math.log1p(520) - Math.log1p(22)),
    0,
    1
  ));
  const exposurePenalty = Math.max(
    darkClipped > 0.28 ? clamp((darkClipped - 0.28) / 0.45, 0, 1) : 0,
    brightClipped > 0.18 ? clamp((brightClipped - 0.18) / 0.4, 0, 1) : 0,
    meanLuminance < 42 ? clamp((42 - meanLuminance) / 35, 0, 1) : 0,
    meanLuminance > 216 ? clamp((meanLuminance - 216) / 34, 0, 1) : 0,
    p95 - p05 < 34 ? clamp((34 - (p95 - p05)) / 28, 0, 1) : 0
  );

  return {
    sharpness: {
      laplacianVariance: Number(laplacianVariance.toFixed(2)),
      score: sharpnessScore,
      isBlurry: laplacianVariance < 42,
    },
    exposure: {
      mean: Number(meanLuminance.toFixed(1)),
      darkClipped: Number(darkClipped.toFixed(4)),
      brightClipped: Number(brightClipped.toFixed(4)),
      dynamicRange: p95 - p05,
      score: Math.round(100 * (1 - exposurePenalty)),
      issue: exposurePenalty > 0.42
        ? (meanLuminance < 100 || darkClipped > brightClipped ? 'underexposed' : 'overexposed')
        : null,
    },
  };
}

function createDifferenceHash(imageData) {
  const { data, width, height } = imageData;
  const columns = 9;
  const rows = 8;
  const samples = new Uint8Array(columns * rows);
  for (let y = 0; y < rows; y += 1) {
    for (let x = 0; x < columns; x += 1) {
      const sourceX = Math.min(width - 1, Math.floor(((x + 0.5) / columns) * width));
      const sourceY = Math.min(height - 1, Math.floor(((y + 0.5) / rows) * height));
      const offset = (sourceY * width + sourceX) * 4;
      samples[y * columns + x] = Math.round(
        data[offset] * 0.2126 + data[offset + 1] * 0.7152 + data[offset + 2] * 0.0722
      );
    }
  }

  let hash = '';
  for (let y = 0; y < rows; y += 1) {
    for (let x = 0; x < columns - 1; x += 1) {
      hash += samples[y * columns + x] > samples[y * columns + x + 1] ? '1' : '0';
    }
  }
  return hash;
}

function hammingDistance(left, right) {
  if (!left || !right || left.length !== right.length) return Infinity;
  let distance = 0;
  for (let index = 0; index < left.length; index += 1) {
    if (left[index] !== right[index]) distance += 1;
  }
  return distance;
}

function scorePhoto(result) {
  let score = 100;
  if (result.sharpness.isBlurry) score -= Math.max(18, Math.round((100 - result.sharpness.score) * 0.42));
  if (result.exposure.issue) score -= Math.max(14, Math.round((100 - result.exposure.score) * 0.34));
  if (result.faceAnalysis?.closedEyeFaces > 0) score -= 32;
  return clamp(score, 0, 100);
}

function classifyEyeState(leftBlink, rightBlink) {
  const average = (leftBlink + rightBlink) / 2;
  return {
    eyesClosed: average >= 0.48 && Math.min(leftBlink, rightBlink) >= 0.3,
    possibleWink: Math.max(leftBlink, rightBlink) >= 0.7 && Math.min(leftBlink, rightBlink) < 0.36,
  };
}

function groupNearDuplicates(results, maxDistance = 6) {
  const groups = [];
  results.forEach((result, index) => {
    let group = groups.find(candidate => {
      const reference = results[candidate[0]];
      const sameShape = Math.abs(reference.aspectRatio - result.aspectRatio) < 0.035;
      return sameShape && hammingDistance(reference.hash, result.hash) <= maxDistance;
    });
    if (!group) {
      group = [];
      groups.push(group);
    }
    group.push(index);
  });

  groups.filter(group => group.length > 1).forEach((group, groupIndex) => {
    const ranked = [...group].sort((left, right) => results[right].qualityScore - results[left].qualityScore);
    ranked.forEach((resultIndex, rank) => {
      results[resultIndex].duplicateGroup = groupIndex + 1;
      results[resultIndex].duplicateRank = rank + 1;
      results[resultIndex].isDuplicateAlternative = rank > 0;
    });
  });
  return results;
}

export { analyzePixels, classifyEyeState, createDifferenceHash, groupNearDuplicates, hammingDistance, scorePhoto };
